import { useContext, useMemo } from "react";
import { ToastContext, ToastContextType } from "@/contexts/ToastContextTypes";

// Hook to use toast notifications
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// Convenience functions for common toast types - now stable with useMemo
export const useToastHelpers = () => {
  const { addToast } = useToast();

  return useMemo(
    () => ({
      success: (title: string, message: string) =>
        addToast({ variant: "success", title, message }),

      error: (title: string, message: string) =>
        addToast({ variant: "error", title, message }),

      warning: (title: string, message: string) =>
        addToast({ variant: "warning", title, message }),

      info: (title: string, message: string) =>
        addToast({ variant: "info", title, message }),

      xp: (xpEarned: number, activity: string) =>
        addToast({
          variant: "xp",
          title: "⭐ XP Earned!",
          message: activity,
          xpEarned,
        }),

      levelUp: (
        xpEarned: number,
        activity: string,
        oldLevel: number,
        newLevel: number,
      ) =>
        addToast({
          variant: "levelup",
          title: "🎊 Level Up!",
          message: activity,
          xpEarned,
          levelUp: true,
          oldLevel,
          newLevel,
        }),

      welcomeBonus: (xpEarned: number, activity: string) =>
        addToast({
          variant: "welcome",
          title: "🎉 Welcome Bonus!",
          message: activity,
          xpEarned,
        }),
    }),
    [addToast],
  );
};
